/**
 * ReviewBlockedCard - Displays when PR review could not be performed
 * 
 * Shows the reason why the review was blocked and recommendations for resolution.
 */
import { cn } from '@/lib/utils'
import { AlertTriangle, XCircle, Info, HelpCircle } from 'lucide-react'

export interface ReviewBlockedData {
  approved?: boolean
  reason?: string
  details?: string
  recommendation?: string
}

export interface ReviewBlockedCardProps {
  data: ReviewBlockedData
  className?: string
}

export function ReviewBlockedCard({ data, className }: ReviewBlockedCardProps) {
  const reason = data.reason || 'Review could not be completed'
  const details = data.details || ''
  const recommendation = data.recommendation || ''

  return (
    <div className={cn(
      "border rounded-lg overflow-hidden",
      "border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30",
      className
    )}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-amber-200 dark:border-amber-800 bg-amber-100 dark:bg-amber-900/30">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          <span className="font-semibold text-amber-800 dark:text-amber-200">Review Blocked</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Reason */}
        <div className="flex items-start gap-2">
          <XCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
          <div>
            <span className="text-sm font-medium text-amber-800 dark:text-amber-200">Reason:</span>
            <p className="text-sm text-amber-700 dark:text-amber-300">{reason}</p>
          </div>
        </div>

        {/* Details */}
        {details && (
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
            <div>
              <span className="text-sm font-medium text-amber-800 dark:text-amber-200">Details:</span>
              <p className="text-sm text-amber-700 dark:text-amber-300">{details}</p>
            </div>
          </div>
        )}

        {/* Recommendation */}
        {recommendation && (
          <div className="flex items-start gap-2 pt-2 border-t border-amber-200 dark:border-amber-800">
            <HelpCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
            <div>
              <span className="text-sm font-medium text-amber-800 dark:text-amber-200">Recommendation:</span>
              <p className="text-sm text-amber-700 dark:text-amber-300">{recommendation}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
